import { clsx } from 'clsx';

import { Stream, Streamable } from '@/vibes/soul/lib/streamable';
import { Link } from '~/components/link';

interface Props {
  className?: string;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  label?: string;
  href: string;
  width: number;
  height: number;
}

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --logo-focus: hsl(var(--primary));
 *   --logo-font-family: var(--font-family-heading);
 *   --logo-text: hsl(var(--foreground));
 * }
 * ```
 */
export function Logo({ className, logo: streamableLogo, href, width, height, label }: Props) {
  return (
    <Stream
      fallback={<div className="h-6 w-16 animate-pulse rounded-md bg-contrast-100" />}
      value={streamableLogo}
    >
      {(logo) => (
        <Link
          aria-label={label}
          className={clsx(
            // inline-flex (not inline-block): callers pass `flex` expecting it to win as
            // it did under v3's utility order; v4 orders inline-block after flex, which
            // let inline-block win and dropped the flex-stretch that sizes the logo img
            'relative inline-flex outline-0 ring-(--logo-focus,hsl(var(--primary))) ring-offset-4 focus-visible:ring-2',
            className,
          )}
          href={href}
          style={typeof logo === 'string' ? {} : { maxWidth: `${width}px`, height: `${height}px` }}
        >
          {typeof logo === 'object' && logo !== null && logo.src !== '' ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={logo.alt}
              className="h-auto w-full object-contain object-left"
              src={logo.src.replace('{:size}', `${width}w`)}
              width={width}
              height={height}
            />
          ) : (
            typeof logo === 'string' && (
              <span className="font-[family-name:var(--logo-font-family,var(--font-family-heading))] text-lg font-medium leading-none text-(--logo-text,hsl(var(--foreground))) @xl:text-2xl">
                {logo}
              </span>
            )
          )}
        </Link>
      )}
    </Stream>
  );
}
